/*
 * File: ui-validate-unique.ts
 * File Created: 2024-09-11 06:29:38
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-10-14 09:28:11
 * Modified By: Val Liu
 * -----
 */

import { ObjectHelper, Retrier } from "@root/core/utilities";
import {
    BaseUITag,
    EUITagRuleType,
    EUITagTarget,
    IUITagInput,
    IUITagResult,
    IUITagMoreTagsUnit,
    UITag,
    EUITagRuleUnion,
} from "../core";
import { IQuery, IQueryDefault } from "@root/core/server/restful";
import config from "@/config/default/serverConfig";

// @uiValidateUnique - make sure this field is unique in the table.
// allow value: null | number (debounce delay) | IUIValidateUnique
// default as: number (debounce delay)

interface IUIValidateUnique {
    "text-validating": string;
    "text-validated": string;
    "text-been-used": string;
    delay: number;
}
const defaultData: IUIValidateUnique = {
    "text-validating": "w_FormUniqueStringValidating",
    "text-validated": "w_FormUniqueStringValidated",
    "text-been-used": "w_FormUniqueStringBeenUsed",
    delay: 500,
};

const QUERY_UNIQUE = Symbol(`_validate_unique_`);
const PREVIOUS_VALUE = Symbol(`_validate_unique_previous_value_`);
const RETRIER = Symbol(`_validate_unique_retrier_`);

@UITag.set()
export class uiValidateUnique extends BaseUITag {
    appliedTo: EUITagTarget[] = [EUITagTarget.FormElement];
    ruleTypes: EUITagRuleUnion[] = [EUITagRuleType.Number, EUITagRuleType.Object];
    allowNullRule: boolean = true;

    parse(input: IUITagInput): IUITagResult | null {
        let rule: any = this.parseRule(input) ?? defaultData.delay;
        /// generize to object
        let ro: IUIValidateUnique = {
            ...defaultData,
            ...(typeof rule === "object" ? rule : { delay: rule }),
        };

        let moreTags: IUITagMoreTagsUnit[] = [];
        const { server, element, hostElement } = input.variables ?? {};
        const { name, row, value } = input.inputs.props ?? {};
        if (
            server == null ||
            element == null ||
            hostElement == null ||
            name == null ||
            value == null ||
            value == ""
        )
            return null;

        const query: IQuery = (element[QUERY_UNIQUE] ??= IQueryDefault());
        const retrier: Retrier = (element[RETRIER] ??= new Retrier(() => {
            const $server = element.$server;
            const params = server.params;
            const path = server.path;
            const value = element[PREVIOUS_VALUE];
            const execQuery = config.mocking ? "logQuery" : "useQuery";

            /// do query
            $server[execQuery](
                query,
                $server.R,
                path as any,
                ObjectHelper.deepMerge(params, {
                    where: {
                        [name]: value,
                    },
                    select: { id: true },
                    take: 1,
                }),
            ).then(() => {
                hostElement.$forceUpdate();
            });

            retrier.stop();
        }, ro.delay));

        const previousValue = element[PREVIOUS_VALUE];
        /// don't make query on edit
        if (row?.id == null && !ObjectHelper.equals(previousValue, value)) {
            element[PREVIOUS_VALUE] = value;
            query.loading = true;
            retrier.restart();
        }

        moreTags.push(
            {
                tag: "uiValidator",
                rule: (o) => {
                    /// don't validate on edit.
                    if (o.row.id) return null;

                    let value = o.value;
                    const { loading, data } = query;

                    if (loading)
                        return {
                            state: false,
                            message: element.$_(ro["text-validating"]),
                        };
                    else if (data) {
                        if (data.results.length === 0)
                            return {
                                state: true,
                                message: element.$_(ro["text-validated"]),
                            };
                        return {
                            state: false,
                            message: element.$_(ro["text-been-used"], [value]),
                        };
                    } else {
                        return undefined;
                    }
                },
            },
            /// disable modification on edit
            {
                tag: "uiDisabled",
                rule: (o) => (o.row.id ? true : false),
            },
        );
        return {
            specials: {
                moreTags,
            },
        };
    }
}
